import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { menuItems } from '../../services/navigation';
import { X } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function Sidebar({ isOpen, onClose }: SidebarProps) {
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-30 md:hidden transition-all duration-300 ${isOpen
            ? 'opacity-100 bg-black/50 backdrop-blur-sm'
            : 'pointer-events-none opacity-0'
          }`}
        onClick={onClose}
        aria-hidden={!isOpen}
      />

      {/* Sidebar panel */}
      <nav
        id="sidebar"
        aria-label="sidebar"
        aria-hidden={!isOpen && typeof window !== 'undefined' && window.innerWidth < 768}
        className={`fixed z-40 flex h-full flex-col bg-surface text-body shadow-2xl transition-transform duration-300 ease-in-out
          md:static md:h-auto md:translate-x-0 md:shadow-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isOpen ? 'w-72 md:w-64' : 'w-72 md:w-20'}
        `}
        style={{
          borderRight: '1px solid rgb(var(--border))',
          paddingTop: `max(env(safe-area-inset-top, 0px), 1rem)`,
          paddingBottom: `max(env(safe-area-inset-bottom, 0px), 1rem)`,
          paddingLeft: '1rem',
          paddingRight: '1rem',
        }}
      >
        {/* Header del sidebar en mobile */}
        <div className="mb-5 flex items-center justify-between md:hidden">
          <p className="text-xs font-bold uppercase tracking-widest text-text-muted">
            Menú Principal
          </p>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-text-muted transition hover:bg-primary/10 hover:text-primary"
            aria-label="Cerrar menú"
          >
            <X size={16} />
          </button>
        </div>

        {isOpen && (
          <p className="mb-4 hidden text-xs font-bold uppercase tracking-widest text-text-muted md:block">
            Menú Principal
          </p>
        )}

        <ul className="flex-1 space-y-1 overflow-y-auto pb-24 md:pb-4">
          {menuItems.map(({ path, label, icon }) => (
            <li key={path}>
              <NavLink
                to={path}
                title={label}
                onClick={() => {
                  if (typeof window !== 'undefined' && window.innerWidth < 768) {
                    onClose();
                  }
                }}
                className={({ isActive }) =>
                  `group flex min-h-[3rem] items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg ${isActive
                    ? 'bg-primary/10 text-primary shadow-sm'
                    : 'text-text-muted hover:bg-muted hover:text-text'
                  } ${isOpen ? 'justify-start' : 'justify-center'}`
                }
              >
                {/* Icon */}
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-text-muted transition-all duration-200 group-[.bg-primary\/10]:border-transparent group-[.bg-primary\/10]:bg-primary group-[.bg-primary\/10]:text-primary-fg group-[.bg-primary\/10]:shadow-md group-hover:border-primary/30 group-hover:bg-primary/5 group-hover:text-primary">
                  {icon}
                </span>
                {/* Label */}
                <span
                  className={`whitespace-nowrap text-left font-medium transition-all duration-300 ${isOpen ? 'ml-1 opacity-100' : 'ml-0 w-0 overflow-hidden opacity-0'
                    }`}
                >
                  {label}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

export default Sidebar;
